import cx from 'classnames';
import * as React from 'react';
import { useTranslation } from 'next-i18next';
import type { IconNames } from '@dx-ui/osc-icon';
import { useIsClient } from 'usehooks-ts';
import { Surface, Link, Button } from '@dx-ui/global-ui-prism';
import type { Surfaces, LinkProps } from '@dx-ui/global-ui-prism';
import { useRouter } from 'next/router';
import { env } from '@dx-ui/framework-env';

type LinkWithExperimentationConfiguration = React.ComponentProps<typeof Link> & {
  experimentationConfiguration?: CmsExperimentationConfiguration;
};

export type SocialMediaLink = LinkProps & {
  channel: (typeof IconNames)[number];
};

export type Footer = {
  /** Allows for a fluid width layout */
  isFluid?: boolean;
  /** The customer support link to use. */
  customerSupportLink?: LinkWithExperimentationConfiguration;
  /** The contact number for the help desk */
  contactPhoneLink?: LinkWithExperimentationConfiguration;
  /** The links displayed horizontally at bottom */
  legalLinks?: React.ComponentProps<typeof Link>[];
  /** The main footer links to be displayed in vertical columns divided equally */
  links?: LinkWithExperimentationConfiguration[];
  /** Social media links to be displayed horizontally under contact information */
  socialLinks?: SocialMediaLink[];
  /** Theme choice for footer */
  surfaceType?: Surfaces;
} & React.ComponentProps<'div'>;

/**
 * A shared footer implementation that is intended to used across OHW UI apps to give a consistent experience for customers to navigate key links.
 *
 * All links within the footer are [Link](/docs/library-components-link--default-story) components.
 */
export const Footer = ({
  ref: forwardedRef,
  customerSupportLink,
  isFluid = false,
  legalLinks,
  links: footerLinks = [],
  contactPhoneLink,
  socialLinks,
  surfaceType,
  ...rest
}: Footer) => {
  const isClient = useIsClient();
  const teConsentRef = React.useRef<HTMLAnchorElement>(null);
  const { t } = useTranslation('osc-footer');
  const { locale = 'en' } = useRouter();
  const links = React.useMemo(
    () =>
      [
        ...footerLinks,
        isClient
          ? ({
              id: 'teconsent',
              url: '',
            } as LinkWithExperimentationConfiguration)
          : null,
      ].filter(Boolean) as LinkWithExperimentationConfiguration[],
    [footerLinks, isClient]
  );

  const footerNavLength = links.length;
  const footerNavHalfPoint = footerNavLength > 6 ? Math.ceil(footerNavLength * 0.5) : 0;
  const footerNavFirstHalf = links.slice(0, footerNavHalfPoint);
  const footerNavSecondHalf = links.slice(footerNavHalfPoint, footerNavLength);

  React.useEffect(() => {
    if (!isClient) return;
    if (!teConsentRef.current) return;
    const callback: MutationCallback = (mutationList) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'childList') {
          teConsentRef.current?.parentElement?.removeAttribute('aria-hidden');
        }
      }
    };
    const observer = new MutationObserver(callback);
    observer.observe(teConsentRef.current, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isClient]);

  return (
    <footer data-testid="osc-footer" ref={forwardedRef} {...rest}>
      <Surface
        type={surfaceType || 'base'}
        className="osc-footer border-t border-solid border-surface"
      >
        <div
          className={cx('pb-8 pt-4', {
            'container-fluid': isFluid,
            container: !isFluid,
          })}
        >
          <div className="mb-10 flex flex-col lg:flex-row">
            <div className="lg:w-1/2">
              <h2 data-osc-product="footer-help-label" className="text--lg">
                {t('helpMessage')}
              </h2>
              <div className="mt-4 md:flex md:divide-x md:divide-surface rtl:divide-x-reverse">
                {!!contactPhoneLink && (
                  <div
                    className="osc-footer w-fit md:px-4 ltr:first:pl-0 ltr:last:pr-0 rtl:first:pr-0 rtl:last:pl-0"
                    {...(isClient && { id: 'callme-wrapper' })}
                  >
                    <Link
                      {...contactPhoneLink}
                      underline={false}
                      className="text--lg whitespace-nowrap font-bold"
                    >
                      <span className="sr-only">{t('phone')}</span>
                      <span className="block uppercase">{contactPhoneLink.label}</span>
                    </Link>
                    <div>
                      <Link
                        url={`${env('OHW_BASE_URL')}${locale}/help-center/global-phone-numbers/`}
                        isNewWindow={true}
                        className="text--sm"
                      >
                        {t('callMessage')}
                      </Link>
                    </div>
                  </div>
                )}
                {!!customerSupportLink && (
                  <div className="mt-4 md:mt-0 md:px-4">
                    <Link
                      {...customerSupportLink}
                      underline={false}
                      className="text--lg whitespace-nowrap font-bold"
                    >
                      {customerSupportLink.label || t('customerSupport')}
                    </Link>
                  </div>
                )}
              </div>
              {socialLinks && socialLinks?.length > 0 && (
                <div className="py-4">
                  <ul className="flex items-center space-x-2 rtl:space-x-reverse">
                    {socialLinks.map(({ channel, url }) => {
                      const socialLinkIconName =
                        channel?.toLowerCase() === 'twitter' ? 'x' : channel?.toLowerCase();
                      return (
                        <li key={`footer-social-${socialLinkIconName}`}>
                          <Button
                            as="Link"
                            fill="flat"
                            url={url}
                            label={socialLinkIconName}
                            className="block"
                            isNewWindow={true}
                            showNewWindowIcon={false}
                            iconOnly={true}
                            iconProps={{
                              name: socialLinkIconName as (typeof IconNames)[number],
                              size: 'xl',
                              variant: 'solid',
                            }}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
            <div className="mt-8 w-full md:flex md:space-x-20 lg:mt-0 lg:w-2/4 lg:justify-end lg:space-x-8 rtl:space-x-reverse">
              {footerNavFirstHalf.length > 0 && (
                <ul>
                  {footerNavFirstHalf.map(
                    ({
                      adaDescription,
                      isNewWindow,
                      label,
                      experimentationConfiguration,
                      ...props
                    }) => (
                      <li
                        className="mb-2"
                        key={label || props?.id || props?.url}
                        {...handleTeconsentParentProps(props)}
                      >
                        <Link
                          {...props}
                          ref={props.id === 'teconsent' ? teConsentRef : undefined}
                          className="text--sm"
                          anchorClassName={cx(props.anchorClassName)}
                          adaDescription={adaDescription}
                          isNewWindow={isNewWindow}
                          data-conductrics-goal={experimentationConfiguration?.goal}
                          data-conductrics-value={experimentationConfiguration?.value}
                        >
                          {label}
                        </Link>
                      </li>
                    )
                  )}
                </ul>
              )}
              {footerNavSecondHalf.length > 0 && (
                <ul>
                  {footerNavSecondHalf.map(
                    ({
                      adaDescription,
                      isNewWindow,
                      label,
                      experimentationConfiguration,
                      ...props
                    }) => (
                      <li
                        className="mb-2"
                        key={label || props.url || props?.id}
                        {...handleTeconsentParentProps(props)}
                      >
                        <Link
                          {...props}
                          ref={props.id === 'teconsent' ? teConsentRef : undefined}
                          className="text--sm"
                          anchorClassName={cx(props.anchorClassName)}
                          adaDescription={adaDescription}
                          isNewWindow={isNewWindow}
                          data-conductrics-goal={experimentationConfiguration?.goal}
                          data-conductrics-value={experimentationConfiguration?.value}
                        >
                          {label}
                        </Link>
                      </li>
                    )
                  )}
                </ul>
              )}
            </div>
          </div>
          {legalLinks && legalLinks.length > 0 && (
            <div>
              <ul className="flex flex-wrap divide-x divide-surface rtl:divide-x-reverse">
                {legalLinks.map(({ adaDescription, label, isNewWindow, ...props }) => (
                  <li
                    className="mt-2 px-4 ltr:first:pl-0 rtl:first:pr-0 rtl:last:px-0"
                    key={label || props.url || props?.id}
                  >
                    <Link
                      {...props}
                      className="text--sm"
                      adaDescription={adaDescription}
                      isNewWindow={isNewWindow}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p
            className={cx('text--sm', {
              'pt-2': legalLinks && legalLinks.length > 0,
            })}
            suppressHydrationWarning
          >
            &copy;{new Date().getFullYear()} Hilton
          </p>
        </div>
      </Surface>
    </footer>
  );
};

function handleTeconsentParentProps(props: React.ComponentProps<typeof Link>) {
  if (props.id === 'teconsent') {
    return {
      'aria-hidden': true,
    };
  }
}

export default Footer;
